import { useState, useMemo } from 'react';
import { demoUsers, demoPayments, demoCanteenBills } from '@/mocks/demoData';
import type { Shift, CanteenBill } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { getShifts, getShiftById } from '@/utils/shiftStore';

function getCanteenBillsForShift(shift: Shift): CanteenBill[] {
  const shiftDate = new Date(shift.created_at).toISOString().slice(0, 10);
  const [startH, startM] = shift.start_time.split(':').map(Number);
  const [endH, endM] = shift.end_time.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  let endMinutes = endH * 60 + endM;
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;

  return demoCanteenBills.filter((b) => {
    const bDate = new Date(b.created_at).toISOString().slice(0, 10);
    if (bDate !== shiftDate) return false;
    if (b.payment_method === 'room_charge') return false;
    const bH = new Date(b.created_at).getHours();
    const bM = new Date(b.created_at).getMinutes();
    let bMinutes = bH * 60 + bM;
    if (bMinutes < startMinutes && endMinutes > 24 * 60) bMinutes += 24 * 60;
    return bMinutes >= startMinutes && bMinutes < endMinutes;
  });
}

export default function ReceptionCash() {
  const { addToast } = useApp();
  const [selectedShift, setSelectedShift] = useState<string | null>(null);

  const shiftList = useMemo(() => getShifts().slice().reverse(), []);

  const getUserName = (userId: string) => demoUsers.find((u) => u.id === userId)?.full_name || 'Unknown';

  const getShiftPayments = (shiftId: string) => {
    const shift = getShiftById(shiftId);
    if (!shift) return [];
    return demoPayments.filter((p) => {
      const pDate = new Date(p.created_at).toISOString().slice(0, 10);
      const sDate = new Date(shift.created_at).toISOString().slice(0, 10);
      return pDate === sDate;
    });
  };

  const handleAdjustClosing = (shiftId: string) => {
    addToast('info', 'Opening closing balance adjustment dialog...');
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Cash Tracking</h1>
        <p className="text-navy-500 mt-1">Shift-wise cash register with payment method breakdown.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {shiftList.map((shift) => {
          const payments = getShiftPayments(shift.id);
          const expected = shift.opening_balance + shift.collections;
          const discrepancy = shift.status === 'closed' ? shift.closing_balance - expected : 0;
          const canteenBills = getCanteenBillsForShift(shift);
          const canteenCash = canteenBills.filter((b) => b.payment_method === 'cash').reduce((s, b) => s + b.total, 0);
          const canteenCard = canteenBills.filter((b) => b.payment_method === 'card').reduce((s, b) => s + b.total, 0);
          const canteenUpi = canteenBills.filter((b) => b.payment_method === 'upi').reduce((s, b) => s + b.total, 0);

          return (
            <div
              key={shift.id}
              onClick={() => setSelectedShift(selectedShift === shift.id ? null : shift.id)}
              className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                selectedShift === shift.id
                  ? 'border-navy-600 ring-2 ring-navy-100'
                  : 'border-navy-100 hover:border-navy-300'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-navy-700">{shift.name}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  shift.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-navy-100 text-navy-600'
                }`}>
                  {shift.status === 'active' ? 'Active' : 'Closed'}
                </span>
              </div>
              <div className="text-xs text-navy-400 mb-3">{getUserName(shift.assigned_to)}</div>

              {/* Opening & Total */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-center">
                  <div className="text-xs text-navy-400 mb-0.5">Opening</div>
                  <div className="text-sm font-semibold text-navy-800">Rs. {shift.opening_balance.toLocaleString('en-IN')}</div>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-arrow-right-line text-navy-300 text-xs" />
                </div>
                <div className="text-center">
                  <div className="text-xs text-navy-400 mb-0.5">Total</div>
                  <div className="text-sm font-semibold text-navy-800">Rs. {shift.collections.toLocaleString('en-IN')}</div>
                </div>
                {shift.status === 'closed' && (
                  <>
                    <div className="flex items-center gap-1">
                      <i className="ri-arrow-right-line text-navy-300 text-xs" />
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-navy-400 mb-0.5">Closing</div>
                      <div className="text-sm font-semibold text-navy-800">Rs. {shift.closing_balance.toLocaleString('en-IN')}</div>
                    </div>
                  </>
                )}
              </div>

              {/* Payment Method Breakdown — 4 cols horizontal */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="bg-green-50 rounded-lg p-2 text-center border border-green-100">
                  <div className="text-[10px] text-green-600 font-medium uppercase tracking-wide mb-0.5">Cash</div>
                  <div className="text-xs font-semibold text-green-700">Rs. {(shift.cash_collections || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center border border-amber-100">
                  <div className="text-[10px] text-amber-600 font-medium uppercase tracking-wide mb-0.5">Card</div>
                  <div className="text-xs font-semibold text-amber-700">Rs. {(shift.card_collections || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 text-center border border-purple-100">
                  <div className="text-[10px] text-purple-600 font-medium uppercase tracking-wide mb-0.5">UPI</div>
                  <div className="text-xs font-semibold text-purple-700">Rs. {(shift.upi_collections || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-teal-50 rounded-lg p-2 text-center border border-teal-100">
                  <div className="text-[10px] text-teal-600 font-medium uppercase tracking-wide mb-0.5">Canteen</div>
                  <div className="text-xs font-semibold text-teal-700">Rs. {(shift.canteen_collections || 0).toLocaleString('en-IN')}</div>
                </div>
              </div>

              {/* Canteen sub-breakdown — horizontal */}
              {shift.canteen_collections > 0 && (
                <div className="flex items-center justify-center gap-3 mb-3 py-1.5 border-t border-dashed border-navy-100">
                  <div className="text-[10px] text-teal-600 font-medium">
                    Canteen Cash: <span className="text-teal-700 font-semibold">Rs. {(shift.canteen_cash || 0).toLocaleString('en-IN')}</span>
                  </div>
                  {shift.canteen_card > 0 && (
                    <div className="text-[10px] text-teal-600 font-medium">
                      Canteen Card: <span className="text-teal-700 font-semibold">Rs. {(shift.canteen_card || 0).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="text-[10px] text-teal-600 font-medium">
                    Canteen UPI: <span className="text-teal-700 font-semibold">Rs. {(shift.canteen_upi || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}

              {shift.status === 'closed' && discrepancy !== 0 && (
                <div className="pt-2 border-t border-navy-100 text-center">
                  <span className={`text-xs font-medium ${discrepancy > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <i className={`${discrepancy > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} mr-1`} />
                    Discrepancy: Rs. {Math.abs(discrepancy).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Shift Details */}
      {selectedShift && (
        <>
          {(() => {
            const shift = getShiftById(selectedShift)!;
            const payments = getShiftPayments(shift.id);
            const canteenBills = getCanteenBillsForShift(shift);
            return (
              <>
                {/* Room Transactions */}
                <div className="bg-white rounded-xl border border-navy-100 overflow-hidden mb-6">
                  <div className="px-5 py-4 border-b border-navy-100 flex items-center justify-between">
                    <h3 className="font-semibold text-navy-800">{shift.name} — Room Transactions</h3>
                    <span className="text-xs text-navy-400">{payments.length} transactions</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-navy-50">
                        <tr>
                          <th className="text-left px-5 py-3 font-semibold text-navy-700">Receipt No</th>
                          <th className="text-left px-5 py-3 font-semibold text-navy-700">Method</th>
                          <th className="text-left px-5 py-3 font-semibold text-navy-700">Type</th>
                          <th className="text-left px-5 py-3 font-semibold text-navy-700">Time</th>
                          <th className="text-right px-5 py-3 font-semibold text-navy-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id} className="border-t border-navy-100">
                            <td className="px-5 py-3 font-medium text-navy-800">{p.receipt_no}</td>
                            <td className="px-5 py-3">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-navy-50 text-navy-600 capitalize">
                                {p.payment_method}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-navy-500 capitalize">{p.payment_type}</td>
                            <td className="px-5 py-3 text-navy-500 text-xs">
                              {new Date(p.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-5 py-3 text-right font-medium text-navy-800">Rs. {p.amount.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                        {payments.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-5 py-8 text-center text-navy-400">No room transactions for this shift.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Canteen Collections Section */}
                <div className="bg-white rounded-xl border border-teal-200 overflow-hidden mb-6">
                  <div className="px-5 py-4 border-b border-teal-100 bg-teal-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                        <i className="ri-restaurant-line text-teal-600" />
                      </span>
                      <h3 className="font-semibold text-teal-800">{shift.name} — Canteen Collections</h3>
                    </div>
                    <span className="text-xs text-teal-600">{canteenBills.length} bills</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-teal-50/30">
                        <tr>
                          <th className="text-left px-5 py-3 font-semibold text-teal-800">Bill No</th>
                          <th className="text-left px-5 py-3 font-semibold text-teal-800">Table/Room</th>
                          <th className="text-left px-5 py-3 font-semibold text-teal-800">Method</th>
                          <th className="text-left px-5 py-3 font-semibold text-teal-800">Time</th>
                          <th className="text-right px-5 py-3 font-semibold text-teal-800">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {canteenBills.map((bill) => (
                          <tr key={bill.id} className="border-t border-teal-50">
                            <td className="px-5 py-3 font-medium text-navy-800">{bill.bill_no}</td>
                            <td className="px-5 py-3 text-navy-500">{bill.table_or_room}</td>
                            <td className="px-5 py-3">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize bg-teal-50 text-teal-700">
                                {bill.payment_method}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-navy-500 text-xs">
                              {new Date(bill.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-5 py-3 text-right font-medium text-teal-700">Rs. {bill.total.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                        {canteenBills.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-5 py-8 text-center text-navy-400">No canteen collections for this shift.</td>
                          </tr>
                        )}
                      </tbody>
                      {canteenBills.length > 0 && (
                        <tfoot>
                          <tr className="border-t-2 border-teal-100 bg-teal-50/30">
                            <td colSpan={4} className="px-5 py-3 text-right font-semibold text-teal-800">Canteen Total</td>
                            <td className="px-5 py-3 text-right font-bold text-teal-800">
                              Rs. {canteenBills.reduce((s, b) => s + b.total, 0).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Closing Balance — only for closed shifts */}
          {(() => {
            const shift = getShiftById(selectedShift)!;
            if (shift.status !== 'closed') return null;
            return (
              <div className="px-5 py-4 border-t border-navy-100 bg-navy-50 rounded-b-xl flex items-center justify-between mb-6 -mt-6 border border-navy-100 border-t-0">
                <div className="text-sm text-navy-600">
                  Expected: Rs. {(shift.opening_balance + shift.collections).toLocaleString('en-IN')}
                </div>
                <button
                  onClick={() => handleAdjustClosing(shift.id)}
                  className="px-3 py-1.5 bg-white border border-navy-200 rounded-md text-xs font-medium text-navy-600 hover:bg-navy-50 cursor-pointer"
                >
                  Adjust Closing Balance
                </button>
              </div>
            );
          })()}
        </>
      )}

      {/* Cash Register Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-navy-100">
          <h3 className="font-semibold text-navy-800">Cash Register Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Shift</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Staff</th>
                <th className="text-right px-4 py-3 font-semibold text-navy-700">Opening</th>
                <th className="text-right px-4 py-3 font-semibold text-green-700">Cash</th>
                <th className="text-right px-4 py-3 font-semibold text-amber-700">Card</th>
                <th className="text-right px-4 py-3 font-semibold text-purple-700">UPI</th>
                <th className="text-right px-4 py-3 font-semibold text-teal-700 whitespace-nowrap">Canteen</th>
                <th className="text-right px-4 py-3 font-semibold text-navy-700">Total</th>
                <th className="text-right px-4 py-3 font-semibold text-navy-700">Closing</th>
                <th className="text-center px-4 py-3 font-semibold text-navy-700">Match</th>
              </tr>
            </thead>
            <tbody>
              {shiftList.map((shift) => {
                const expected = shift.opening_balance + shift.collections;
                const matched = shift.status === 'closed' ? shift.closing_balance === expected : null;
                return (
                  <tr key={shift.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-navy-800">{shift.name}</td>
                    <td className="px-5 py-3.5 text-navy-600">{getUserName(shift.assigned_to)}</td>
                    <td className="px-4 py-3.5 text-right">Rs. {shift.opening_balance.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-right text-green-600">Rs. {(shift.cash_collections || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-right text-amber-600">Rs. {(shift.card_collections || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-right text-purple-600">Rs. {(shift.upi_collections || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-right text-teal-600">Rs. {(shift.canteen_collections || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-right font-medium">Rs. {shift.collections.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-right">Rs. {shift.closing_balance.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3.5 text-center">
                      {matched === null ? (
                        <span className="text-navy-400 text-xs">Active</span>
                      ) : matched ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                          <i className="ri-checkbox-circle-line" /> Match
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                          <i className="ri-error-warning-line" /> Mismatch
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}